import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { AdminLayout } from '../layout/AdminLayout';
import { bookingApi, invoiceApi } from '../../lib/api';
import { formatCurrency, formatDate } from '../../lib/utils';
import { toast } from 'sonner';
import {
    ArrowLeft, User, Mail, MapPin, CreditCard, Receipt, FileText,
    Plus, Loader2, Save, Video, History, Download, RotateCw, CheckCircle2, XCircle,
    Award, TrendingUp, Calendar, ChevronRight, ChevronLeft, Banknote
} from 'lucide-react';import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import PaymentDialog from '../dialogs/PaymentDialog';

const PAYMENT_METHODS = {
    ueberweisung: 'Überweisung',
    karte: 'Kartenzahlung',
    bar: 'Barzahlung',
};

const STATUS_BADGE = {
    paid: { label: 'Bezahlt', cls: 'bg-green-100 text-green-700' },
    partial: { label: 'Teilbezahlt', cls: 'bg-amber-100 text-amber-700' },
    open: { label: 'Offen', cls: 'bg-gray-100 text-gray-700' },
};

export default function AdminParticipantDetailPage() {
    const { seminarId, bookingId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    // Blätterfunktion: gefilterte Teilnehmer-Liste aus Router-State oder sessionStorage lesen.
    // Session-Persistenz überlebt Browser-Reloads und das Zurückspringen zwischen den
    // Teilnehmer-Detail-Seiten. Kontext bleibt an den Seminar-ID gebunden.
    const navStorageKey = `bgpr_participant_nav_${seminarId}`;
    const navContext = useMemo(() => {
        const fromState = location.state && Array.isArray(location.state.bookingIds) ? location.state : null;
        if (fromState) {
            try { sessionStorage.setItem(navStorageKey, JSON.stringify(fromState)); } catch { /* */ }
            return fromState;
        }
        try {
            const raw = sessionStorage.getItem(navStorageKey);
            if (raw) {
                const parsed = JSON.parse(raw);
                if (parsed && Array.isArray(parsed.bookingIds)) return parsed;
            }
        } catch { /* */ }
        return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [seminarId, location.state]);

    const bookingIds = navContext?.bookingIds || [];
    const currentIdx = bookingIds.indexOf(bookingId);
    const hasPrev = currentIdx > 0;
    const hasNext = currentIdx >= 0 && currentIdx < bookingIds.length - 1;
    const filterLabel = navContext?.filterLabel || 'Teilnehmer';

    const goToNeighbour = (delta) => {
        const next = bookingIds[currentIdx + delta];
        if (!next) return;
        navigate(`/admin/seminars/${seminarId}/participants/${next}`, {
            state: {
                bookingIds,
                filterLabel,
                seminarId,
            },
        });
    };

    // Tastatur-Shortcuts: ← / → blättern zwischen Teilnehmern.
    // Ignoriert Eingaben in Textfeldern, damit Notizen nicht versehentlich navigieren.
    useEffect(() => {
        const onKey = (e) => {
            const t = e.target;
            const inEditable = t && (
                t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable
            );
            if (inEditable) return;
            if (e.metaKey || e.ctrlKey || e.altKey) return;
            if (e.key === 'ArrowLeft' && hasPrev) { e.preventDefault(); goToNeighbour(-1); }
            else if (e.key === 'ArrowRight' && hasNext) { e.preventDefault(); goToNeighbour(1); }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasPrev, hasNext, currentIdx, bookingIds.join('|')]);

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [note, setNote] = useState('');
    const [noteSaving, setNoteSaving] = useState(false);
    const [payOpen, setPayOpen] = useState(false);
    const [directPayOpen, setDirectPayOpen] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await bookingApi.getDetail(bookingId);
            setData(res.data);
            setNote(res.data?.booking?.note || '');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Fehler beim Laden');
        } finally {
            setLoading(false);
        }
    }, [bookingId]);

    useEffect(() => { load(); }, [load]);

    const saveNote = async () => {
        setNoteSaving(true);
        try {
            await bookingApi.saveNote(bookingId, note);
            toast.success('Bemerkung gespeichert');
        } catch {
            toast.error('Speichern fehlgeschlagen');
        } finally {
            setNoteSaving(false);
        }
    };

    const requestPayment = async (sendEmail) => {
        try {
            await bookingApi.createInvoice(bookingId, sendEmail);
            toast.success(sendEmail ? 'Zahlungsforderung angelegt & versendet' : 'Zahlungsforderung angelegt');
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Fehler');
        }
    };

    const revertInvoicePayment = async (inv, pay) => {
        const amountLabel = formatCurrency(pay.amount);
        const wasFullyPaid = inv.status === 'paid';
        const receiptHint = wasFullyPaid
            ? '\n\n⚠️ Die Quittung wird zurückgezogen: Nummer entfernt, Portal-Link nicht mehr abrufbar, Dropbox-Datei gelöscht.'
            : '';
        const confirmed = window.confirm(
            `Zahlungseingang zurücknehmen?\n\n`
            + `Betrag: ${amountLabel}\n`
            + `Datum: ${formatDate(pay.date)}\n`
            + (pay.reference ? `Referenz: ${pay.reference}\n` : '')
            + receiptHint
            + `\n\nDiese Aktion wird protokolliert.`
        );
        if (!confirmed) return;
        const reason = window.prompt('Grund für die Rücknahme (optional, für Audit-Log):', '') || '';
        try {
            const res = await invoiceApi.revertPayment(inv.id, pay.id, reason);
            const d = res.data || {};
            const parts = [];
            if (d.receipt_removed) parts.push('Quittungsnummer entfernt');
            if (d.dropbox_receipt_deleted === true) parts.push('Dropbox-Quittung gelöscht');
            else if (d.dropbox_receipt_deleted === false) parts.push('⚠ Dropbox-Löschung fehlgeschlagen (Log prüfen)');
            toast.success(`Zahlung zurückgenommen${parts.length ? ' · ' + parts.join(' · ') : ''}`);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Rücknahme fehlgeschlagen');
        }
    };

    const resendMeeting = async () => {
        try {
            await bookingApi.resendMeetingLink(bookingId);
            toast.success('Meeting-Link erneut versendet');
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Versand fehlgeschlagen');
        }
    };

    const toggleReferent = async (checked) => {
        try {
            await bookingApi.setReferent(bookingId, checked);
            toast.success(checked ? 'Als Referent markiert — keine Seminargebühr' : 'Referenten-Status zurückgenommen');
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Fehler beim Ändern des Referenten-Status');
        }
    };

    const resendEmailLog = async (logId) => {
        try {
            await bookingApi.resendEmailLog(logId);
            toast.success('E-Mail erneut versendet');
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erneuter Versand fehlgeschlagen');
        }
    };

    const downloadReceiptFor = async (bId) => {
        try {
            const res = await bookingApi.downloadReceipt(bId);
            const blob = new Blob([res.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url; a.download = `Quittung_${bId.slice(0,8)}.pdf`;
            document.body.appendChild(a); a.click();
            window.URL.revokeObjectURL(url); document.body.removeChild(a);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Beleg konnte nicht geladen werden');
        }
    };

    const downloadCertificateFor = async (bId) => {
        try {
            const res = await bookingApi.downloadCertificate(bId);
            const blob = new Blob([res.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url; a.download = `Bescheinigung_${bId.slice(0,8)}.pdf`;
            document.body.appendChild(a); a.click();
            window.URL.revokeObjectURL(url); document.body.removeChild(a);
            // Reload, damit `certificate_generated`-Flag in der History aktuell ist.
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Bescheinigung konnte nicht geladen werden');
        }
    };

    const sendCertificateByEmailFor = async (bId) => {
        const ok = window.confirm('Möchten Sie diesem Teilnehmer die Bescheinigung erneut per E-Mail zusenden?');
        if (!ok) return;
        try {
            const res = await bookingApi.sendCertificateByEmail(bId);
            toast.success(res.data?.message || 'Bescheinigung versendet');
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Versand fehlgeschlagen');
        }
    };

    const downloadInvoicePdf = async (invoiceId, invoiceNumber) => {
        try {
            const res = await invoiceApi.downloadPdf(invoiceId);
            const blob = new Blob([res.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url; a.download = `Quittung_${invoiceNumber || invoiceId.slice(0,8)}.pdf`;
            document.body.appendChild(a); a.click();
            window.URL.revokeObjectURL(url); document.body.removeChild(a);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'PDF konnte nicht geladen werden');
        }
    };

    if (loading || !data) {
        return <AdminLayout><div className="flex items-center justify-center h-96"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div></AdminLayout>;
    }

    const { booking, participant, seminar, invoices, email_logs, history, stats } = data;
    const p = participant || {};
    const openInvoice = invoices.find(i => i.status !== 'paid');
    // Compute expected fee: booking.final_price → seminar.price → 0
    // This ensures Seminargebühr shows even without an invoice, and updates live if seminar.price changes
    const expectedFee = booking.final_price != null ? booking.final_price : (seminar?.price || 0);
    const totalPaid = invoices.reduce((sum, i) => sum + (i.paid_amount || 0), 0);
    const invoiceAmount = invoices.reduce((sum, i) => sum + (i.amount || 0), 0);
    // If no invoice yet → show expectedFee. Else show invoiceAmount (the fixed-at-creation amount).
    const displayedFee = invoices.length === 0 ? expectedFee : invoiceAmount;
    const totalOpen = Math.max(0, displayedFee - totalPaid);
    const hasOnlineOrHybrid = (seminar?.dates || []).some(d => d.format === 'online' || d.format === 'hybrid');
    const fullName = `${p.title ? p.title + ' ' : ''}${p.first_name || ''} ${p.last_name || ''}`.trim();

    return (
        <AdminLayout>
        <div className="flex flex-col gap-8 max-w-6xl" data-testid="participant-detail-page">
            {/* Header */}
            <div className="flex items-center gap-3 flex-wrap">
                <Button variant="ghost" size="sm" onClick={() => navigate(`/admin/seminars/${seminarId}?tab=participants`)} data-testid="back-to-seminar">
                    <ArrowLeft className="h-4 w-4 mr-1" /> Zurück zur Teilnehmerliste
                </Button>
                <div className="flex-1 min-w-[220px]">
                    <h1 className="text-2xl font-bold" data-testid="participant-name">{fullName || 'Teilnehmer'}</h1>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Mail className="h-3 w-3" />
                        <span>{p.email || booking.participant_email || '—'}</span>
                        {booking.status === 'confirmed' && <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Angemeldet</span>}
                        {booking.status === 'cancelled' && <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">Storniert</span>}
                    </div>
                </div>
                {bookingIds.length > 1 && currentIdx >= 0 && (
                    <div className="flex items-center gap-1 border rounded-lg px-1 py-1 bg-muted/30" data-testid="participant-pager">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => goToNeighbour(-1)}
                            disabled={!hasPrev}
                            data-testid="participant-prev-btn"
                            title="Vorheriger Teilnehmer (←)"
                        >
                            <ChevronLeft className="h-4 w-4 mr-1" /> Vorheriger
                        </Button>
                        <span className="px-2 text-xs text-muted-foreground whitespace-nowrap" data-testid="participant-pager-position">
                            <span className="font-semibold text-foreground">{currentIdx + 1}</span>
                            {' von '}{bookingIds.length}
                            <span className="hidden md:inline"> · {filterLabel}</span>
                        </span>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => goToNeighbour(1)}
                            disabled={!hasNext}
                            data-testid="participant-next-btn"
                            title="Nächster Teilnehmer (→)"
                        >
                            Nächster <ChevronRight className="h-4 w-4 ml-1" />
                        </Button>
                    </div>
                )}
            </div>

            {/* Stats-Chips: Kontext auf einen Blick */}
            {stats && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="participant-stats">
                    <StatChip
                        icon={Calendar}
                        label="Buchungen gesamt"
                        value={stats.booking_count ?? 0}
                        tone="neutral"
                    />
                    <StatChip
                        icon={CheckCircle2}
                        label="Seminare besucht"
                        value={stats.attended_count ?? 0}
                        tone="success"
                    />
                    <StatChip
                        icon={Award}
                        label="Aktive Lizenzen"
                        value={stats.licenses_active ?? 0}
                        tone={stats.licenses_active > 0 ? 'primary' : 'neutral'}
                        popoverContent={stats.licenses_active > 0 ? (
                            <div className="space-y-2" data-testid="licenses-popover">
                                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Aktive Lizenzen</p>
                                <div className="divide-y">
                                    {(stats.licenses || []).map((lic) => {
                                        const expiry = lic.valid_until;
                                        const today = new Date().toISOString().slice(0, 10);
                                        const daysLeft = expiry ? Math.ceil((new Date(expiry) - new Date(today)) / (1000*60*60*24)) : null;
                                        const expSoon = daysLeft !== null && daysLeft <= 90;
                                        return (
                                            <div key={lic.id} className="py-2 text-sm" data-testid={`license-${lic.id}`}>
                                                <div className="flex items-center justify-between gap-3">
                                                    <div className="min-w-0">
                                                        <p className="font-medium truncate">{lic.license_type || 'Lizenz'}</p>
                                                        <p className="text-xs text-muted-foreground font-mono truncate">{lic.license_number}</p>
                                                    </div>
                                                    <div className={`text-xs text-right shrink-0 ${expSoon ? 'text-amber-700 font-semibold' : 'text-muted-foreground'}`}>
                                                        {expiry ? (
                                                            <>
                                                                gültig bis<br/>
                                                                <span className="font-mono">{formatDate(expiry)}</span>
                                                                {daysLeft !== null && (
                                                                    <span className="block text-[10px] mt-0.5">
                                                                        {daysLeft >= 0 ? `noch ${daysLeft} Tage` : `${Math.abs(daysLeft)} Tage abgelaufen`}
                                                                    </span>
                                                                )}
                                                            </>
                                                        ) : <span>unbefristet</span>}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ) : null}
                    />
                    <StatChip
                        icon={TrendingUp}
                        label="Umsatz gesamt"
                        value={formatCurrency(stats.total_paid ?? 0)}
                        tone={stats.total_paid > 0 ? 'success' : 'neutral'}
                        popoverContent={(stats.paid_invoices || []).length > 0 ? (
                            <div className="space-y-2" data-testid="revenue-popover">
                                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Zahlungseingänge</p>
                                <div className="divide-y max-h-80 overflow-y-auto">
                                    {(stats.paid_invoices || []).map((pi, idx) => (
                                        <div key={`${pi.invoice_id}-${idx}`} className="py-2 text-sm flex items-center gap-3" data-testid={`revenue-item-${pi.invoice_id}`}>
                                            <div className="min-w-0 flex-1">
                                                <p className="font-medium truncate">{pi.seminar_title || '(ohne Seminar)'}</p>
                                                <p className="text-xs text-muted-foreground">
                                                    <span className="font-mono">{pi.receipt_number || pi.invoice_number || '—'}</span>
                                                    {pi.payment_date && <span> · {formatDate(pi.payment_date)}</span>}
                                                </p>
                                            </div>
                                            <div className="text-right shrink-0">
                                                <p className="font-semibold">{formatCurrency(pi.amount)}</p>
                                                <button
                                                    type="button"
                                                    onClick={(e) => { e.stopPropagation(); downloadInvoicePdf(pi.invoice_id, pi.receipt_number || pi.invoice_number); }}
                                                    className="inline-flex items-center gap-1 text-[11px] text-primary hover:underline"
                                                    data-testid={`revenue-pdf-${pi.invoice_id}`}
                                                >
                                                    <Download className="h-3 w-3" /> PDF
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : null}
                    />
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Persönliche Daten + Adresse */}
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-base"><User className="h-4 w-4" /> Persönliche Daten</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3 text-sm">
                        <Row label="Anrede" value={p.salutation} />
                        <Row label="Titel" value={p.title} />
                        <Row label="Vorname" value={p.first_name} />
                        <Row label="Nachname" value={p.last_name} />
                        <Row label="Geburtsdatum" value={p.birth_date ? formatDate(p.birth_date) : ''} />
                        <Row label="EFN" value={p.efn} />
                        <div className="border-t pt-3 mt-3 space-y-3">
                            <div className="flex items-center gap-2 font-medium text-muted-foreground text-xs uppercase tracking-wider"><MapPin className="h-3 w-3" />Adresse</div>
                            <Row label="Straße" value={p.street} />
                            <Row label="PLZ / Ort" value={`${p.postal_code || ''} ${p.city || ''}`.trim()} />
                            <Row label="Land" value={p.country || 'Deutschland'} />
                        </div>
                        <div className="border-t pt-3 mt-3 space-y-3">
                            <div className="flex items-center gap-2 font-medium text-muted-foreground text-xs uppercase tracking-wider"><Mail className="h-3 w-3" />Kontakt</div>
                            <Row label="E-Mail" value={p.email} />
                            <Row label="Telefon" value={p.phone} />
                            <Row label="Mobil" value={p.mobile} />
                            <Row label={'„tätig für"'} value={p.club} />
                        </div>
                    </CardContent>
                </Card>

                {/* Anmeldung & Zahlung */}
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-base"><CreditCard className="h-4 w-4" /> Anmeldung & Zahlung</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {/* Referenten-Toggle — wenn aktiviert, entfällt die gesamte
                             Zahlungsabwicklung. Teilnehmerstatus bleibt grün und
                             am Ende wird ganz normal eine Bescheinigung ausgestellt. */}
                        <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition ${booking.is_referent ? 'bg-emerald-50 border-emerald-300' : 'bg-gray-50 border-gray-200 hover:bg-gray-100'}`}>
                            <input
                                type="checkbox"
                                checked={!!booking.is_referent}
                                onChange={(e) => toggleReferent(e.target.checked)}
                                className="mt-0.5 w-4 h-4"
                                data-testid="booking-is-referent-toggle"
                            />
                            <div className="text-sm">
                                <p className={`font-medium ${booking.is_referent ? 'text-emerald-900' : 'text-gray-800'}`}>
                                    Teilnahme als Referent
                                </p>
                                <p className={`text-xs mt-0.5 ${booking.is_referent ? 'text-emerald-700' : 'text-gray-500'}`}>
                                    {booking.is_referent
                                        ? 'Keine Seminargebühr fällig. Buchung läuft wie bei bezahlten Teilnehmern (Bescheinigung & UE-Gutschrift am Seminarende).'
                                        : 'Aktivieren, wenn dieser Teilnehmer als Referent auftritt — Seminargebühr entfällt automatisch.'}
                                </p>
                            </div>
                        </label>

                        <div className="text-sm space-y-2">
                            <Row label="Seminar" value={seminar?.title} />
                            <Row label="Kursnummer" value={seminar?.course_number} />
                            <Row label="Anmeldedatum" value={booking.created_at ? formatDate(booking.created_at) : ''} />
                            {booking.selected_discount && (
                                <Row label="Rabatt" value={`${booking.selected_discount.name} (−${booking.selected_discount.discount_value}${booking.selected_discount.discount_type === 'percent' ? '%' : ' €'})`} />
                            )}
                            {booking.verein_name && <Row label="Verein" value={booking.verein_name} />}
                        </div>

                        {booking.is_referent ? (
                            <div className="p-4 rounded-lg bg-emerald-50 border border-emerald-200 text-sm text-emerald-900" data-testid="referent-no-fee-info">
                                <p className="font-semibold flex items-center gap-2">
                                    <CreditCard className="h-4 w-4" />
                                    Keine Seminargebühr — Referent
                                </p>
                                <p className="text-xs mt-1 text-emerald-800">
                                    Diese Buchung erscheint nicht in Umsatz-/Offene-Zahlungsforderungen-Statistiken. Teilnahmestatus + Bescheinigung werden wie bei regulären Teilnehmern gehandhabt.
                                </p>
                            </div>
                        ) : (
                            <>
                                {/* Payment summary cards */}
                                <div className="grid grid-cols-3 gap-2">
                                    <SumCard label="Seminargebühr" value={formatCurrency(displayedFee)} tone="neutral" />
                                    <SumCard label="Bezahlt" value={formatCurrency(totalPaid)} tone="success" />
                                    <SumCard label="Offen" value={formatCurrency(totalOpen)} tone={totalOpen > 0 ? 'warning' : 'neutral'} />
                                </div>

                        {/* Invoice list */}
                        {invoices.length === 0 ? (
                            <div className="border border-dashed rounded-lg p-4 space-y-3">
                                <div className="text-center">
                                    <p className="text-sm text-muted-foreground">Noch keine Zahlungsforderung versandt</p>
                                    <p className="text-xs text-muted-foreground mt-0.5">
                                        Aktuelle Seminargebühr: <span className="font-semibold text-foreground">{formatCurrency(expectedFee)}</span>
                                        {' · '}passt sich automatisch an Änderungen der Seminargebühr an
                                    </p>
                                </div>
                                <div className="flex flex-wrap gap-2 justify-center">
                                    <Button size="sm" onClick={() => requestPayment(false)} variant="outline" data-testid="create-invoice-btn">
                                        <Plus className="h-3 w-3 mr-1" /> Nur anlegen (ohne Mail)
                                    </Button>
                                    <Button size="sm" onClick={() => requestPayment(true)} data-testid="create-invoice-send-btn">
                                        <Mail className="h-3 w-3 mr-1" /> Zahlungsforderung anlegen & per Mail senden
                                    </Button>
                                    <Button size="sm" variant="secondary" onClick={() => setDirectPayOpen(true)} data-testid="direct-pay-btn">
                                        <CreditCard className="h-3 w-3 mr-1" /> Zahlungseingang direkt verbuchen
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {invoices.map(inv => {
                                    const sb = STATUS_BADGE[inv.status] || STATUS_BADGE.open;
                                    const isPaid = inv.status === 'paid';
                                    return (
                                        <div key={inv.id} className="border rounded-lg p-3 space-y-2" data-testid={`invoice-${inv.id}`}>
                                            <div className="flex items-start justify-between">
                                                <div>
                                                    <p className="text-xs text-muted-foreground">{isPaid && inv.receipt_number ? `Quittung ${inv.receipt_number}` : 'Offene Zahlung'}</p>
                                                    <p className="font-bold text-lg">{formatCurrency(inv.amount)}</p>
                                                </div>
                                                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${sb.cls}`}>{sb.label}</span>
                                            </div>
                                            {/* Payment history */}
                                            {(inv.payments && inv.payments.length > 0) && (
                                                <div className="border-t pt-2 space-y-1">
                                                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Zahlungshistorie</p>
                                                    {inv.payments.map((pay, idx) => (
                                                        <div key={pay.id || idx} className="flex items-center justify-between text-xs group">
                                                            <span>{formatDate(pay.date)} · {PAYMENT_METHODS[pay.method] || pay.method}{pay.reference ? ` · ${pay.reference}` : ''}</span>
                                                            <div className="flex items-center gap-2">
                                                                <span className="font-semibold">{formatCurrency(pay.amount)}</span>
                                                                {pay.id && (
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => revertInvoicePayment(inv, pay)}
                                                                        className="text-red-600 hover:text-red-800 opacity-60 hover:opacity-100 text-[10px] underline decoration-dotted underline-offset-2"
                                                                        title="Fehlbuchung zurücknehmen"
                                                                        data-testid={`revert-payment-${pay.id}`}
                                                                    >
                                                                        Rücknahme
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                            {/* Actions */}
                                            <div className="flex gap-2">
                                                {!isPaid && (
                                                    <Button size="sm" variant="outline" onClick={() => setPayOpen(inv.id)} data-testid={`pay-invoice-${inv.id}`}>
                                                        <Plus className="h-3 w-3 mr-1" /> Zahlung erfassen
                                                    </Button>
                                                )}
                                                {isPaid && (
                                                    <Button size="sm" variant="ghost" onClick={async () => {
                                                        try {
                                                            const res = await invoiceApi.downloadPdf(inv.id);
                                                            const blob = new Blob([res.data], { type: 'application/pdf' });
                                                            const url = URL.createObjectURL(blob);
                                                            window.open(url);
                                                        } catch { toast.error('PDF-Fehler'); }
                                                    }} title="Quittung herunterladen" data-testid={`download-invoice-${inv.id}`}>
                                                        <FileText className="h-3 w-3 mr-1" /> Quittung
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                            </>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Actions row (only if available) */}
            {hasOnlineOrHybrid && (
                <Card>
                    <CardContent className="py-4 flex flex-wrap gap-2">
                        <Button variant="outline" size="sm" onClick={resendMeeting} data-testid="resend-meeting-btn">
                            <Video className="h-4 w-4 mr-1" /> Meeting-Link erneut senden
                        </Button>
                    </CardContent>
                </Card>
            )}

            {/* Bemerkungen */}
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-base"><Receipt className="h-4 w-4" /> Bemerkungen</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                    <Textarea
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder="Interne Notizen..."
                        rows={3}
                        data-testid="note-textarea"
                    />
                    <Button size="sm" onClick={saveNote} disabled={noteSaving} data-testid="save-note-btn">
                        {noteSaving ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Save className="h-3 w-3 mr-1" />} Speichern
                    </Button>
                </CardContent>
            </Card>

            {/* Seminar-Historie dieses Teilnehmers */}
            {history && history.length > 0 && (
                <Card>
                    <CardHeader className="flex flex-row items-center justify-between gap-2">
                        <CardTitle className="flex items-center gap-2 text-base"><History className="h-4 w-4" /> Seminar-Historie ({history.length})</CardTitle>
                        <span className="text-xs text-muted-foreground">weitere Buchungen dieses Teilnehmers</span>
                    </CardHeader>
                    <CardContent>
                        <div className="divide-y">
                            {history.map(h => {
                                const isAttended = h.attended;
                                const catLabel = h.event_category === 'ausbildung' ? 'Lizenz' : 'Bescheinigung';
                                return (
                                    <div key={h.booking_id} className="py-3 flex items-center gap-3 flex-wrap" data-testid={`history-row-${h.booking_id}`}>
                                        <div className="flex-1 min-w-[220px]">
                                            <button
                                                onClick={() => navigate(`/admin/seminars/${h.seminar_id}/participants/${h.booking_id}`)}
                                                className="text-sm font-medium hover:text-primary text-left"
                                            >
                                                {h.seminar_title}
                                            </button>
                                            <div className="text-xs text-muted-foreground flex gap-2 flex-wrap">
                                                {h.course_number && <span className="font-mono">{h.course_number}</span>}
                                                {h.seminar_date && <span>· {formatDate(h.seminar_date)}</span>}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2 text-xs">
                                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded ${isAttended ? 'bg-green-100 text-green-800' : h.status === 'cancelled' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}`}>
                                                {isAttended ? <CheckCircle2 className="h-3 w-3" /> : h.status === 'cancelled' ? <XCircle className="h-3 w-3" /> : null}
                                                {isAttended ? 'Teilgenommen' : h.status === 'cancelled' ? 'Storniert' : 'Angemeldet'}
                                            </span>
                                            {h.invoice_status && (
                                                <span className={`px-2 py-0.5 rounded ${h.invoice_status === 'paid' ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`}>
                                                    {h.invoice_status === 'paid' ? 'Bezahlt' : h.invoice_status === 'partial' ? 'Teilbezahlt' : 'Offen'}
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-1">
                                            {h.invoice_status === 'paid' && (
                                                <Button size="sm" variant="outline" onClick={() => downloadReceiptFor(h.booking_id)} data-testid={`history-receipt-${h.booking_id}`}>
                                                    <Download className="h-3 w-3 mr-1" /> Beleg
                                                </Button>
                                            )}
                                            {isAttended && (
                                                <>
                                                    <Button size="sm" variant="outline" onClick={() => downloadCertificateFor(h.booking_id)} data-testid={`history-cert-${h.booking_id}`} title="Bescheinigung als PDF herunterladen / neu generieren">
                                                        <FileText className="h-3 w-3 mr-1" /> {catLabel}
                                                    </Button>
                                                    <Button size="sm" variant="outline" onClick={() => sendCertificateByEmailFor(h.booking_id)} data-testid={`history-cert-mail-${h.booking_id}`} title="Bescheinigung per E-Mail an Teilnehmer senden">
                                                        <Mail className="h-3 w-3 mr-1" /> Per Mail senden
                                                    </Button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* E-Mail-Verlauf */}
            {email_logs && email_logs.length > 0 && (
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-base"><Mail className="h-4 w-4" /> E-Mail-Verlauf ({email_logs.length})</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-2">
                            {email_logs.slice(0, 25).map(log => {
                                const canResend = ['confirmation', 'payment_request', 'meeting_link', 'payment_confirmation'].includes(log.template_type);
                                return (
                                    <div key={log.id} className="flex items-start gap-2 text-sm border-b last:border-0 pb-2" data-testid={`email-log-${log.id}`}>
                                        <span className={`text-xs font-medium px-2 py-0.5 rounded ${log.status === 'sent' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                            {log.status === 'sent' ? '✓' : '⚠'}
                                        </span>
                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium truncate">{log.subject || '(kein Betreff)'}</p>
                                            <p className="text-xs text-muted-foreground">
                                                {log.template_type} · {log.sent_at ? formatDate(log.sent_at) : '—'}
                                                {log.recipient_email && <span> · {log.recipient_email}</span>}
                                            </p>
                                        </div>
                                        {canResend && (
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => resendEmailLog(log.id)}
                                                data-testid={`resend-email-${log.id}`}
                                                title="E-Mail erneut versenden"
                                            >
                                                <RotateCw className="h-3 w-3 mr-1" /> Erneut senden
                                            </Button>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Payment dialog – gemeinsame Komponente, identisch zur Buchhaltung */}
            <PaymentDialog
                open={!!payOpen}
                onOpenChange={(o) => setPayOpen(o ? payOpen : false)}
                invoice={invoices.find(i => i.id === payOpen)}
                participantName={p.first_name || p.last_name ? `${p.first_name || ''} ${p.last_name || ''}`.trim() : ''}
                seminarTitle={seminar?.title}
                onSuccess={load}
            />

            {/* Direct payment (no invoice yet) dialog */}
            <DirectPaymentDialog
                open={directPayOpen}
                onOpenChange={setDirectPayOpen}
                bookingId={bookingId}
                expectedFee={expectedFee}
                onSuccess={load}
            />
        </div>
        </AdminLayout>
    );
}

function StatChip({ icon: Icon, label, value, tone = 'neutral', popoverContent }) {
    const toneMap = {
        success: 'bg-green-50 text-green-800 border-green-200',
        primary: 'bg-primary/10 text-primary border-primary/20',
        warning: 'bg-amber-50 text-amber-800 border-amber-200',
        neutral: 'bg-muted/40 text-foreground border-border',
    };
    const clickable = Boolean(popoverContent);
    const inner = (
        <div className={`rounded-lg border px-4 py-3 flex items-center gap-3 transition-colors ${toneMap[tone]} ${clickable ? 'cursor-pointer hover:border-primary/60' : ''}`} data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
            {Icon && <Icon className="h-5 w-5 opacity-80 shrink-0" />}
            <div className="min-w-0 flex-1">
                <p className="text-[10px] uppercase tracking-wider opacity-75 leading-tight">{label}</p>
                <p className="font-bold text-lg leading-tight truncate">{value}</p>
            </div>
            {clickable && <ChevronRight className="h-3.5 w-3.5 opacity-50" />}
        </div>
    );
    if (!clickable) return inner;
    return (
        <Popover>
            <PopoverTrigger asChild>{inner}</PopoverTrigger>
            <PopoverContent className="w-80" align="start">
                {popoverContent}
            </PopoverContent>
        </Popover>
    );
}


function Row({ label, value }) {
    if (!value) return null;
    return (
        <div className="flex justify-between gap-4">
            <span className="text-muted-foreground text-xs uppercase tracking-wider">{label}</span>
            <span className="font-medium text-right">{value}</span>
        </div>
    );
}

function SumCard({ label, value, tone }) {
    const toneClass = tone === 'success' ? 'bg-green-50 text-green-800 border-green-200'
        : tone === 'warning' ? 'bg-amber-50 text-amber-800 border-amber-200'
        : 'bg-muted/40 text-foreground';
    return (
        <div className={`rounded-lg border p-3 text-center ${toneClass}`}>
            <p className="text-xs uppercase tracking-wider mb-0.5 opacity-75">{label}</p>
            <p className="font-bold text-base">{value}</p>
        </div>
    );
}

// PaymentDialog wurde nach /components/dialogs/PaymentDialog.jsx ausgelagert,
// damit Buchhaltung & Teilnehmer-Detail garantiert dieselbe UI verwenden.

function DirectPaymentDialog({ open, onOpenChange, bookingId, expectedFee, onSuccess }) {
    const today = new Date().toISOString().split('T')[0];
    const [form, setForm] = useState({ paid_amount: expectedFee || 0, payment_date: today, payment_method: 'ueberweisung', payment_reference: '' });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            setForm({ paid_amount: expectedFee || 0, payment_date: today, payment_method: 'ueberweisung', payment_reference: '' });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, expectedFee]);

    const submit = async () => {
        if (!form.paid_amount || form.paid_amount <= 0) {
            toast.error('Bitte einen gültigen Betrag eingeben');
            return;
        }
        setSaving(true);
        try {
            await bookingApi.recordPayment(bookingId, form);
            toast.success('Zahlungseingang verbucht');
            onOpenChange(false);
            onSuccess?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Fehler');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle>Zahlungseingang verbuchen</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-2">
                    <p className="text-xs text-muted-foreground">
                        Eine Zahlungsforderung wird automatisch im Hintergrund angelegt und sofort mit dieser Zahlung verrechnet.
                    </p>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                            <Label>Betrag *</Label>
                            <Input type="number" step="0.01" min="0" value={form.paid_amount} onChange={e => setForm(f => ({ ...f, paid_amount: parseFloat(e.target.value) || 0 }))} data-testid="direct-pay-amount" />
                        </div>
                        <div className="space-y-1.5">
                            <Label>Datum *</Label>
                            <Input type="date" value={form.payment_date} onChange={e => setForm(f => ({ ...f, payment_date: e.target.value }))} />
                        </div>
                    </div>
                    <div className="space-y-1.5">
                        <Label>Zahlungsart</Label>
                        <Select value={form.payment_method} onValueChange={v => setForm(f => ({ ...f, payment_method: v }))}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="ueberweisung">Überweisung</SelectItem>
                                <SelectItem value="karte">Kartenzahlung</SelectItem>
                                <SelectItem value="bar">Barzahlung</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-1.5">
                        <Label>Referenz / Verwendungszweck (optional)</Label>
                        <Input value={form.payment_reference} onChange={e => setForm(f => ({ ...f, payment_reference: e.target.value }))} />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
                    <Button onClick={submit} disabled={saving} data-testid="direct-pay-submit">
                        {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
                        Verbuchen
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
